import setFieldValue from "@iewc/Store/Handlers/InstantRegistrationForm/SetFieldValue";
import { CustomActions, CustomState } from "@iewc/Store/Reducers";
import { colorStrings } from "@iewc/Utilities/variables";
import { OwnProps, setInstantFormEffects } from "@iewc/Widgets/CreateAccount/FormBaseComponent";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import getColor from "@insite/mobius/utilities/getColor";
import * as React from "react";
import { useEffect } from "react";
import { connect, DispatchProp, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomState) => ({
    accountNumber: state.customInstantRegistrationForm.fieldValues.accountNumber,
    validAccountNumber: state.customInstantRegistrationForm.fieldValues.validAccountNumber,
});

const mapDispatchToProps = {
    setFieldValue,
};

export interface AccountNumberStyles {
    accountNumberLabel?: TextFieldProps;
    accountNumberTooltip?: TooltipPresentationProps;
}

export const accountNumberStyles: AccountNumberStyles = {
    accountNumberLabel: {
        cssOverrides: {
            formField: css`
                label {
                    display: flex;
                    align-items: center;
                }
            `,
        },
    },
    accountNumberTooltip: {
        cssOverrides: {
            tooltipWrapper: css`
                margin-left: 5px;

                button:hover,
                button:focus {
                    outline: none;
                }

                button svg {
                    color: ${getColor(colorStrings.secondaryMain)};
                }
            `,
        },
    },
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    DispatchProp<CustomActions>;

const accountNumberRegex = /^\d{5}$/;

const AccountNumberWidget: React.FC<Props> = ({
    accountNumber,
    validAccountNumber,
    setFieldValue: widgetSetFieldValue,
}) => {
    const label = translate("Account Number");
    const isRequired = true;
    const [fieldErrorMessage, setFieldErrorMessage] = React.useState("");
    const [validAccount, setValidAccount] = React.useState("");

    const validateaccountNumber = () => {
        let errorMessage: any = "";
        if (!accountNumber && isRequired) {
            errorMessage = siteMessage("InstantRegForm_AccountNumberIsRequiredErrorMessage", label);
        } else if (accountNumber && !accountNumberRegex.test(accountNumber)) {
            errorMessage = siteMessage("InstantRegForm_AccountNumber5digitsErrorMessage");
        } else if (accountNumber && accountNumber === "54080") {
            errorMessage = siteMessage("InstantRegForm_AccountNumberIsInvalidErrorMessage");
        } else if (accountNumber && validAccount !== "" && !validAccount) {
            errorMessage = siteMessage("InstantRegForm_AccountNumberFailedValidationErrorMessage");
        }

        setFieldErrorMessage(errorMessage as string);
        return !errorMessage;
    };

    setInstantFormEffects(isRequired, accountNumber, "accountNumber", validateaccountNumber);

    const fieldChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setValidAccount("");
        widgetSetFieldValue({ key: "accountNumber", value: event.target.value });
    };

    const fieldBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {        
        setValidAccount("");
        validateaccountNumber();
    };

    useEffect(() => {
        if (validAccountNumber !== undefined) {
            setValidAccount(validAccountNumber);
        }
    }, [validAccountNumber]);
    
    useEffect(() => {
        if(validAccount !== "") {
            validateaccountNumber();
        }
    }, [validAccount]);

    return (
        <TextField
            label={
                <>
                    {label}
                    {" *"}
                    <Tooltip
                        {...accountNumberStyles.accountNumberTooltip}
                        text={siteMessage("accountNumber_TooltipMessage").toString()}
                    />
                </>
            }
            value={accountNumber || ""}
            error={fieldErrorMessage}
            onChange={fieldChangeHandler}
            onBlur={fieldBlurHandler}
            id="accountNumber"
        />
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(AccountNumberWidget);
